import { createApproximateMeasurer, DEFAULT_TEXT_STYLE, flatColor, needsChartPanel, paintEnvironment, } from '@orbit-mind/map-engine';
import { DEFAULT_EXPORT_PALETTE, exportViewBox } from '../svg';
import { A4_PORTRAIT, buildPdf, landscape, num, winAnsi, } from './document';
export * from './document';
export const DEFAULT_PDF_MARGIN = 36;
function hexToRgb(hex) {
    const value = hex.replace('#', '');
    const full = value.length === 3
        ? value
            .split('')
            .map((c) => c + c)
            .join('')
        : value;
    const r = Number.parseInt(full.slice(0, 2), 16) / 255;
    const g = Number.parseInt(full.slice(2, 4), 16) / 255;
    const b = Number.parseInt(full.slice(4, 6), 16) / 255;
    return `${num(r)} ${num(g)} ${num(b)}`;
}
/** A rounded rectangle as four lines and four Bézier corners. */
function roundedRect(rect, radius) {
    const r = Math.min(radius, rect.width / 2, rect.height / 2);
    if (r <= 0) {
        return `${num(rect.x)} ${num(rect.y)} ${num(rect.width)} ${num(rect.height)} re`;
    }
    // The circle-to-Bézier constant. Anything else makes corners that are visibly not round.
    const k = r * 0.5523;
    const { x, y, width: w, height: h } = rect;
    return [
        `${num(x + r)} ${num(y)} m`,
        `${num(x + w - r)} ${num(y)} l`,
        `${num(x + w - r + k)} ${num(y)} ${num(x + w)} ${num(y + r - k)} ${num(x + w)} ${num(y + r)} c`,
        `${num(x + w)} ${num(y + h - r)} l`,
        `${num(x + w)} ${num(y + h - r + k)} ${num(x + w - r + k)} ${num(y + h)} ${num(x + w - r)} ${num(y + h)} c`,
        `${num(x + r)} ${num(y + h)} l`,
        `${num(x + r - k)} ${num(y + h)} ${num(x)} ${num(y + h - r + k)} ${num(x)} ${num(y + h - r)} c`,
        `${num(x)} ${num(y + r)} l`,
        `${num(x)} ${num(y + r - k)} ${num(x + r - k)} ${num(y)} ${num(x + r)} ${num(y)} c`,
        'h',
    ].join('\n');
}
function connectionOps(link, palette) {
    const stroke = link.color ?? (link.kind === 'hierarchy' ? palette.hierarchyLink : palette.crossLink);
    const ops = [
        'q',
        `${hexToRgb(stroke)} RG`,
        `${num(link.width ?? 1.5)} w`,
        '1 J',
        '1 j',
    ];
    ops.push(pathOps(link.segments));
    ops.push('S', 'Q');
    return ops.join('\n');
}
/**
 * A path, as PDF operators.
 *
 * The one place segments become `m`/`l`/`c`, so a connection and a node outline cannot disagree
 * about how a curve is written — and so that adding a fourth segment kind is one edit rather than
 * a hunt through the file.
 */
function pathOps(segments) {
    return segments
        .map((segment) => {
        if (segment.kind === 'move')
            return `${num(segment.to.x)} ${num(segment.to.y)} m`;
        if (segment.kind === 'line')
            return `${num(segment.to.x)} ${num(segment.to.y)} l`;
        return (`${num(segment.control1.x)} ${num(segment.control1.y)} ` +
            `${num(segment.control2.x)} ${num(segment.control2.y)} ` +
            `${num(segment.to.x)} ${num(segment.to.y)} c`);
    })
        .join('\n');
}
/**
 * A node's content as PDF operators.
 *
 * Opacity is mixed into the colour against the node's fill: a page has no transparency without an
 * extra graphics-state object per shade, and a tint over a card is the same pixels as the mixed
 * colour. A picture is left as its placeholder — the exporter has no file bytes to embed.
 */
function primitiveOps(primitives, ink, under, measurer) {
    // Series colours step for a dark fill; a wash is its average tint. No gradients on this page.
    const environment = paintEnvironment(ink, under);
    const colour = (paint, opacity) => hexToRgb(flatColor(paint, environment, opacity ?? 1));
    const ops = [];
    let unsupported = 0;
    for (const primitive of primitives) {
        switch (primitive.kind) {
            case 'rect':
            case 'path':
            case 'circle': {
                const shape = primitive.kind === 'rect'
                    ? roundedRect(primitive.rect, primitive.radius ?? 0)
                    : primitive.kind === 'path'
                        ? pathOps(primitive.segments)
                        : roundedRect({
                            x: primitive.cx - primitive.r,
                            y: primitive.cy - primitive.r,
                            width: primitive.r * 2,
                            height: primitive.r * 2,
                        }, primitive.r);
                const fill = primitive.fill;
                const stroke = primitive.stroke;
                if (fill === undefined && stroke === undefined)
                    break;
                ops.push('q', '1 J', '1 j');
                if (primitive.kind === 'path' && primitive.dash !== undefined) {
                    ops.push(`[${primitive.dash.map(num).join(' ')}] 0 d`, '0 J');
                }
                if (fill !== undefined)
                    ops.push(`${colour(fill, primitive.opacity)} rg`);
                if (stroke !== undefined) {
                    ops.push(`${colour(stroke, primitive.opacity)} RG`, `${num(primitive.strokeWidth ?? 1)} w`);
                }
                ops.push(shape, fill !== undefined && stroke !== undefined ? 'B' : fill !== undefined ? 'f' : 'S', 'Q');
                break;
            }
            case 'text': {
                const encoded = winAnsi(primitive.text);
                unsupported += encoded.unsupported;
                const style = {
                    ...DEFAULT_TEXT_STYLE,
                    fontSize: primitive.size,
                    fontWeight: primitive.weight,
                };
                const width = measurer.measure(primitive.text, style, Number.POSITIVE_INFINITY).width;
                const x = primitive.anchor === 'start'
                    ? primitive.x
                    : primitive.anchor === 'end'
                        ? primitive.x - width
                        : primitive.x - width / 2;
                ops.push('q', `${colour(primitive.fill, primitive.opacity)} rg`, 'BT', `/F1 ${num(primitive.size)} Tf`, `1 0 0 -1 ${num(x)} ${num(primitive.y)} Tm`, `(${encoded.literal}) Tj`, 'ET', 'Q');
                break;
            }
            case 'image':
                break;
        }
    }
    return { ops, unsupported };
}
function nodeOps(node, palette, measurer, encode, page = {
    ink: palette.text,
    background: palette.background,
}) {
    const { rect, textStyle } = node;
    // The scene's own number, as in the SVG export. A PDF that rounded its boxes differently from
    // the screen would be a file that is subtly not the map somebody exported.
    const radius = node.cornerRadius;
    const bare = node.bare === true;
    // A bare node — a station's name, a heading — is only its mark and its words.
    const ops = bare
        ? []
        : [
            'q',
            `${hexToRgb(node.style.fill ?? palette.nodeFill)} rg`,
            `${hexToRgb(node.style.stroke ?? palette.nodeStroke)} RG`,
            `${num(node.style.strokeWidth ?? 1)} w`,
            '1 j',
            // A rounded rectangle where one will do, and the scene's own segments where it will not.
            // Written by the same function that writes a connection's path, so a hexagon on a page is
            // built from the numbers the canvas drew rather than from a second idea of what one is.
            node.outline === null ? roundedRect(rect, radius) : pathOps(node.outline),
            // Fill and stroke in one go, so the border sits exactly on the shape that was filled.
            'B',
            'Q',
        ];
    let unsupported = 0;
    if (node.glyph !== undefined && node.glyph.length > 0) {
        const glyph = bare
            ? primitiveOps(node.glyph, page.ink, page.background, measurer)
            : primitiveOps(node.glyph, node.style.textColor ?? palette.text, node.style.fill ?? palette.nodeFill, measurer);
        ops.push(...glyph.ops);
        unsupported += glyph.unsupported;
    }
    const ink = node.style.textColor ?? palette.text;
    const fill = node.style.fill ?? palette.nodeFill;
    // A chart on a saturated card sits on a neutral panel, as on screen.
    const panel = node.content?.kind === 'chart' && needsChartPanel(fill) ? node.content.rect : null;
    if (panel !== null) {
        ops.push('q', `${hexToRgb(palette.nodeFill)} rg`, `${hexToRgb(palette.nodeStroke)} RG`, '0.75 w', roundedRect({ x: panel.x - 5, y: panel.y - 5, width: panel.width + 10, height: panel.height + 10 }, 8), 'B', 'Q');
    }
    const content = primitiveOps(node.content?.primitives ?? [], panel === null ? ink : palette.text, panel === null ? fill : palette.nodeFill, measurer);
    const link = primitiveOps(node.link?.primitives ?? [], ink, fill, measurer);
    ops.push(...content.ops, ...link.ops);
    unsupported += content.unsupported + link.unsupported;
    if (node.lines.length > 0) {
        const lineHeight = textStyle.fontSize * 1.35;
        const textBox = node.textBox ?? rect;
        const centreX = textBox.x + textBox.width / 2;
        const firstY = textBox.y + textBox.height / 2 - ((node.lines.length - 1) * lineHeight) / 2;
        ops.push('q', `${hexToRgb(node.style.textColor ?? (bare ? page.ink : palette.text))} rg`, 'BT');
        ops.push(`/F1 ${num(textStyle.fontSize)} Tf`);
        node.lines.forEach((line, index) => {
            const custom = encode?.(line, textStyle.fontSize);
            const encoded = custom === undefined ? winAnsi(line) : { literal: '', unsupported: 0 };
            unsupported += encoded.unsupported;
            // No `text-anchor` in PDF: centring is arithmetic, and the width has to come from the same
            // measurer that decided how wide the box is.
            const width = custom?.width ?? measurer.measure(line, textStyle, Number.POSITIVE_INFINITY).width;
            const x = centreX - width / 2;
            // The page transform flips y; this flips the glyphs back the right way up. `+ 0.35em`
            // approximates the baseline offset that `dominant-baseline: central` gives on the canvas.
            const y = firstY + index * lineHeight + textStyle.fontSize * 0.35;
            ops.push(`1 0 0 -1 ${num(x)} ${num(y)} Tm`, custom?.operations ?? `(${encoded.literal}) Tj`);
        });
        ops.push('ET', 'Q');
    }
    return { ops: ops.join('\n'), unsupported };
}
export function pdfPagePlan(view, options = {}) {
    const margin = options.margin ?? DEFAULT_PDF_MARGIN;
    const requested = options.page ?? A4_PORTRAIT;
    // A mind map is wider than it is tall far more often than not, and a portrait page would waste
    // half of itself. The map decides, not a default.
    const size = view.width > view.height ? landscape(requested) : requested;
    const usable = { width: size.width - margin * 2, height: size.height - margin * 2 };
    const pagination = options.pagination ?? { mode: 'fit' };
    if (![view.x, view.y, view.width, view.height, margin, usable.width, usable.height].every(Number.isFinite) ||
        view.width <= 0 ||
        view.height <= 0 ||
        margin < 0 ||
        usable.width <= 0 ||
        usable.height <= 0)
        throw new Error('Invalid PDF bounds');
    if (pagination.mode === 'fit') {
        const scale = Math.min(usable.width / view.width, usable.height / view.height);
        return [{ size, scale, window: view }];
    }
    const scale = pagination.scale ?? 1;
    if (!Number.isFinite(scale) || scale <= 0)
        throw new Error('Invalid PDF scale');
    const perPage = { width: usable.width / scale, height: usable.height / scale };
    const columns = Math.max(1, Math.ceil(view.width / perPage.width));
    const rows = Math.max(1, Math.ceil(view.height / perPage.height));
    if (columns * rows > 100)
        throw new Error('PDF limited to 100 pages');
    const placements = [];
    // Left to right, then top to bottom: the order they leave a printer is the order they go on a
    // wall.
    for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
            placements.push({
                size,
                scale,
                window: {
                    x: view.x + column * perPage.width,
                    y: view.y + row * perPage.height,
                    width: perPage.width,
                    height: perPage.height,
                },
            });
        }
    }
    return placements;
}
/** Does any part of this rectangle fall inside the window? */
function within(rect, window) {
    return (rect.x < window.x + window.width &&
        rect.x + rect.width > window.x &&
        rect.y < window.y + window.height &&
        rect.y + rect.height > window.y);
}
export function pdfPages(scene, options = {}, encode) {
    const palette = { ...DEFAULT_EXPORT_PALETTE, ...options.palette };
    const measurer = options.measurer ?? createApproximateMeasurer();
    const margin = options.margin ?? DEFAULT_PDF_MARGIN;
    const view = exportViewBox(scene.bounds, options.padding);
    let unsupportedCharacters = 0;
    const background = scene.canvas?.background ?? palette.background;
    const page = {
        ink: scene.canvas?.dark === true ? '#f1f2f5' : palette.text,
        background,
    };
    const pages = pdfPagePlan(view, options).map((placement) => {
        const { size, scale, window } = placement;
        // Flip y, then place the window's top-left at the page's top-left inside the margin. A PDF's
        // origin is bottom-left; the scene's is top-left, and everything downstream assumes the
        // scene's.
        const tx = margin - window.x * scale;
        const ty = size.height - margin + window.y * scale;
        const body = [
            'q',
            `${hexToRgb(scene.canvas?.background ?? palette.background)} rg`,
            `0 0 ${num(size.width)} ${num(size.height)} re`,
            'f',
            'Q',
            'q',
            `${num(scale)} 0 0 ${num(-scale)} ${num(tx)} ${num(ty)} cm`,
        ];
        // A structure's picture — lines, lanes, a road — under everything else.
        if (scene.underlay !== undefined && scene.underlay.length > 0) {
            const drawn = primitiveOps(scene.underlay, page.ink, page.background, measurer);
            unsupportedCharacters += drawn.unsupported;
            body.push(...drawn.ops);
        }
        // Only what this page shows. On a single fitted page that is everything; on a tiled wall it
        // is what keeps page four from carrying the whole map's worth of operators.
        for (const link of scene.connections) {
            const bounds = boundsOfSegments(link);
            if (!within(bounds, window))
                continue;
            body.push(connectionOps(link, palette));
        }
        for (const node of scene.nodes) {
            if (!within(node.rect, window))
                continue;
            const drawn = nodeOps(node, palette, measurer, encode, page);
            unsupportedCharacters += drawn.unsupported;
            body.push(drawn.ops);
        }
        body.push('Q');
        return { size, content: body.join('\n') };
    });
    return { pages, unsupportedCharacters };
}
export function sceneToPdf(scene, options = {}) {
    const result = pdfPages(scene, options);
    return {
        bytes: buildPdf(result.pages, options.title === undefined ? {} : { title: options.title }),
        pages: result.pages.length,
        unsupportedCharacters: result.unsupportedCharacters,
    };
}
/** A crude box around a route, enough to decide whether a page shows any of it. */
function boundsOfSegments(link) {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    const consider = (x, y) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
    };
    for (const segment of link.segments) {
        consider(segment.to.x, segment.to.y);
        if (segment.kind === 'cubic') {
            consider(segment.control1.x, segment.control1.y);
            consider(segment.control2.x, segment.control2.y);
        }
    }
    if (!Number.isFinite(minX))
        return { x: 0, y: 0, width: 0, height: 0 };
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
//# sourceMappingURL=index.js.map